import { theme as defaultTheme } from "@mindlercare/ui-web";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import PageHeader from "components/lib/pageHeader";
import { endOfDay, getUnixTime, startOfDay, sub } from "date-fns";
import { useRefundDialogParam } from "hooks/useRefundDialogParam";
import { Filters } from "../shared/Filters";
import Table from "../shared/tableBackendFilter";
import { Filter } from "../shared/types";
import { getPayments } from "./api";
import {
  formatPaymentTableData,
  getPaymentTableColumns,
  getPaymentTableFilters,
  getPaymentTableSensitiveFields,
} from "./model";
import { Payment } from "./types";
import { RefundDialog } from "./RefundDialog";
import { useCurrentCountryId } from "core/selectors/marketSelectors";

const DEBOUNCE_MS = 1000;

const initialFiltersState = {
  patientFirstName: "",
  patientLastName: "",
  pageNumber: 1,
  itemsPerPage: 100,
  psychologistFirstName: "",
  psychologistLastName: "",
  slotId: "",
  userId: "",
  dateTo: getUnixTime(endOfDay(new Date())),
  dateFrom: getUnixTime(startOfDay(sub(new Date(), { days: 10 }))),
};

export const PaymentsPage = () => {
  const { t } = useTranslation();
  const [refundPaymentId, setRefundPaymentIdParam] = useRefundDialogParam();
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [nationalIdentity, setNationalIdentity] = useState<string | null>(null);
  const [filters, setFilters] = useState(initialFiltersState);
  const [payments, setPayments] = useState<Payment[]>([]);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const countryId = useCurrentCountryId();

  useEffect(() => {
    if (refundPaymentId !== null) return;

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      handleRunFilters();
    }, DEBOUNCE_MS);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refundPaymentId]);

  const onClick = (row: Payment) => {
    const payment = payments.find(
      (aPayment) => aPayment.paymentId === row.paymentId,
    );
    if (!payment) return;
    setSelectedPayment(payment);
    setRefundPaymentIdParam(payment.paymentId);
  };

  const handleChangeFilter = (id: string, rawValue: string) => {
    if (id === "dateFrom" || id === "dateTo") {
      setFilters({
        ...filters,
        [id]: getUnixTime(new Date(rawValue)),
      });
    } else if (id === "nationalIdentity") {
      setNationalIdentity(rawValue);
    } else {
      setFilters({
        ...filters,
        [id]: rawValue,
      });
    }
  };

  const handleRunFilters = async () => {
    try {
      const payload = nationalIdentity
        ? {
            nationalIdentity,
          }
        : null;

      const res = await getPayments({ ...filters }, payload);
      setPayments(res);
    } catch {
      setPayments([]);
    }
  };

  return (
    <Root>
      <PageHeader hasSensitive={false} translation={t("PAYMENTS")} mb={6} />
      <Filters
        filters={getPaymentTableFilters(t, filters) as Filter[]}
        onApplyFilters={handleRunFilters}
        onChangeFilter={handleChangeFilter}
      />

      <Table
        tableColumns={getPaymentTableColumns(t)}
        rows={formatPaymentTableData(payments, t, countryId)}
        onClick={onClick}
        rowsPerPage={100}
        filters={getPaymentTableFilters(t, filters)}
        sensitiveFields={getPaymentTableSensitiveFields()}
      />

      {refundPaymentId !== null && selectedPayment && (
        <RefundDialog payment={selectedPayment} />
      )}
    </Root>
  );
};

const Root = styled.div`
  min-height: calc(100% - 71px);
  background: white;
  padding: ${(p) => defaultTheme.PADDING.XL};
  @media (max-width: ${(p) => defaultTheme.BREAKPOINTS.MD}) {
    padding: ${(p) => defaultTheme.PADDING.SM} ${(p) => defaultTheme.PADDING.MD};
  }
`;
